import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface StreakPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  children: React.ReactNode;
}

export const StreakPopover: React.FC<StreakPopoverProps> = ({
  anchorRef,
  onClose,
  children,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [visible, setVisible] = useState(false);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const gap = 8;

    let top = anchorRect.bottom + gap;
    let left = anchorRect.left + anchorRect.width / 2 - popoverRect.width / 2;

    // Edge-clamp horizontal
    const padding = 12;
    if (left < padding) left = padding;
    if (left + popoverRect.width > window.innerWidth - padding) {
      left = window.innerWidth - padding - popoverRect.width;
    }

    // Edge-clamp vertical — if not enough room below, show above
    if (top + popoverRect.height > window.innerHeight - padding) {
      top = anchorRect.top - gap - popoverRect.height;
    }

    setPosition({ top, left });
  }, [anchorRef]);

  // Calculate position once the popover is mounted
  useEffect(() => {
    // Initial position calculation after DOM paint
    requestAnimationFrame(() => {
      updatePosition();
      // Trigger entrance animation after position is set
      requestAnimationFrame(() => setVisible(true));
    });
  }, [updatePosition]);

  // Click-outside dismissal
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose, anchorRef]);

  // Escape key dismissal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <>
      {/* Mobile backdrop */}
      <div
        className="fixed inset-0 bg-black/10 z-[60] sm:hidden"
        aria-hidden="true"
      />
      <div
        ref={popoverRef}
        role="dialog"
        aria-modal="false"
        className="fixed z-[60] bg-popover border border-border rounded-xl shadow-lg"
        style={{
          top: position ? `${position.top}px` : '-9999px',
          left: position ? `${position.left}px` : '-9999px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(-4px)',
          transition: 'opacity 150ms ease-out, transform 150ms ease-out',
        }}
      >
        {children}
      </div>
    </>,
    document.body
  );
};
